import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDateLongLao, formatInBangkok } from "@/shared/lib/date-time";
import { Button, confirm, Dialog, DialogContent, toast } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { EyeIcon, EyeOffIcon, TrashIcon } from "lucide-react";
import {
  useDeleteReview,
  useReviewQuery,
  useUpdateReviewHidden,
} from "../api/queries";
import { StarRating } from "./StarRating";

type ReviewDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reviewId: string | null;
};

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
      {children}
    </span>
  );
}

export function ReviewDetailDialog({
  open,
  onOpenChange,
  reviewId,
}: ReviewDetailDialogProps) {
  const review = useReviewQuery(reviewId ?? "");
  const canManage = useActionPermission(["reviews:update", "reviews:delete"]);
  const updateHidden = useUpdateReviewHidden();
  const deleteReview = useDeleteReview();

  const r = review.data;
  const isLoading = review.isLoading;

  const handleToggleHidden = () => {
    if (!r) return;
    const newHidden = !r.hidden;
    toast.promise(updateHidden.mutateAsync({ id: r.id, hidden: newHidden }), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: newHidden ? "ຊ້ອນສຳເລັດ" : "ເປີດໃຫ້ສະແດງສຳເລັດ",
      error: "ປ່ຽນສະຖານະລົ້ມເຫຼວ",
    });
  };

  const handleDelete = async () => {
    if (!r) return;
    const ok = await confirm({
      title: "ລຶບຄຳເຫັນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຄຳເຫັນນີ້?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      const promise = deleteReview.mutateAsync(r.id);
      toast.promise(promise, {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບສຳເລັດ",
        error: "ລຶບລົ້ມເຫຼວ",
      });
      promise.then(() => onOpenChange(false));
    }
  };

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-2xl">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="flex items-start justify-between border-b pb-4">
            <div>
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                ລະບົບກວດກາຄຳເຫັນ
              </p>
              <h2 className="mt-0.5 font-bold text-xl uppercase tracking-wide">
                ລາຍລະອຽດຄຳເຫັນ
              </h2>
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              ກຳລັງໂຫລດ...
            </div>
          ) : !r ? (
            <div className="py-12 text-center text-muted-foreground">
              ບໍ່ພົບຄຳເຫັນ
            </div>
          ) : (
            <>
              <div className="grid gap-6 py-6 sm:grid-cols-3">
                {/* Left column */}
                <div className="space-y-5 border-r pr-6 sm:col-span-1">
                  <div>
                    <Label>ຜູ້ເຂົ້າຮ່ວມ</Label>
                    <p className="font-semibold">
                      {r.authorDisplayName ?? r.authorName ?? "—"}
                    </p>
                    {r.authorEmail && (
                      <p className="text-muted-foreground text-sm">
                        {r.authorEmail}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>ສິນຄ້າທີ່ກ່ຽວຂ້ອງ</Label>
                    <Link
                      to="/app/catalog/products/$id"
                      params={{ id: r.productId }}
                      onClick={handleClose}
                      className="font-semibold underline underline-offset-2 hover:no-underline"
                    >
                      {r.productName ?? r.productSlug ?? "—"}
                    </Link>
                  </div>
                  <div>
                    <Label>ວັນທີສົ່ງ</Label>
                    <p>{formatDateLongLao(r.createdAt)}</p>
                    <p className="text-muted-foreground text-sm">
                      {formatInBangkok(r.createdAt, "HH:mm")} ນ.ທ.
                    </p>
                  </div>
                  <div>
                    <Label>ຄະແນນ</Label>
                    <StarRating
                      rating={r.rating}
                      showNumber={false}
                      size="md"
                    />
                  </div>
                </div>

                {/* Right column */}
                <div className="sm:col-span-2">
                  <Label>ເນື້ອໃນຄຳເຫັນ</Label>
                  <div className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">
                    {r.content ?? "—"}
                  </div>
                </div>
              </div>

              {/* Footer */}
              {canManage && (
                <div className="flex flex-wrap gap-2 border-t pt-4">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleToggleHidden}
                    disabled={updateHidden.isPending}
                  >
                    {r.hidden ? (
                      <>
                        <EyeIcon className="mr-2 h-4 w-4" />
                        ເປີດໃຫ້ສະແດງ
                      </>
                    ) : (
                      <>
                        <EyeOffIcon className="mr-2 h-4 w-4" />
                        ຊ້ອນ
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    disabled={deleteReview.isPending}
                  >
                    <TrashIcon className="mr-2 h-4 w-4" />
                    ລຶບ
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
